import { Injectable } from '@angular/core';
import { Actions, concatLatestFrom, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { HousingActions, MarginCalculatorApiActions, MarginCalculatorPageActions } from '../actions';
import { concatMap, of, switchMap } from 'rxjs';
import * as fromSelectors from '../selectors';
import { HousingStateHelpers } from '../state/housing-state-helpers';

@Injectable()
export class HousingEffects {
    setDefaultHousingExpenses$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(MarginCalculatorApiActions.marginLoadedSuccess),
            concatLatestFrom(() => [
                this.store.select(fromSelectors.selectMoveOutExpenses),
                this.store.select(fromSelectors.selectBedrooms),
                this.store.select(fromSelectors.selectContinuedHousing)
            ]),
            switchMap(([, moveOutExpenses, bedrooms, continuedHousing]) => {
                if (moveOutExpenses > 0) {
                    return of(
                        MarginCalculatorPageActions.noOp({
                            debug: 'setDefaultHousingExpenses$ effect - moveOutExpenses already set'
                        })
                    );
                } else if (continuedHousing) {
                    return of(
                        HousingActions.setMoveOutExpenses({
                            moveOutExpenses: 0
                        })
                    );
                }
                return of(
                    HousingActions.setMoveOutExpenses({
                        moveOutExpenses: HousingStateHelpers.getMoveOutExpenses(bedrooms)
                    })
                );
            })
        );
    });

    setDefaultFurniture$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(MarginCalculatorApiActions.marginLoadedSuccess),
            concatLatestFrom(() => [
                this.store.select(fromSelectors.selectFurniturePerMonth),
                this.store.select(fromSelectors.selectBedrooms),
                this.store.select(fromSelectors.selectContinuedHousing)
            ]),
            switchMap(([, furniture, bedrooms, continuedHousing]) => {
                if (furniture > 0) {
                    return of(
                        MarginCalculatorPageActions.noOp({
                            debug: 'setDefaultFurniture$ effect - furniture already set'
                        })
                    );
                } else if (continuedHousing) {
                    return of(
                        HousingActions.setFurniture({
                            furniture: 0
                        })
                    );
                }
                return of(
                    HousingActions.setFurniture({
                        furniture: HousingStateHelpers.getFurniture(bedrooms)
                    })
                );
            })
        );
    });

    setDefaultFurnitureDelivery$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(MarginCalculatorApiActions.marginLoadedSuccess),
            concatLatestFrom(() => [
                this.store.select(fromSelectors.selectFurnitureDelivery),
                this.store.select(fromSelectors.selectBedrooms),
                this.store.select(fromSelectors.selectContinuedHousing)
            ]),
            switchMap(([, furnitureDelivery, bedrooms, continuedHousing]) => {
                if (furnitureDelivery > 0) {
                    return of(
                        MarginCalculatorPageActions.noOp({
                            debug: 'setDefaultFurnitureDelivery$ effect - furnitureDelivery already set'
                        })
                    );
                } else if (continuedHousing) {
                    return of(
                        HousingActions.setFurnitureDelivery({
                            furnitureDelivery: 0
                        })
                    );
                }
                return of(
                    HousingActions.setFurnitureDelivery({
                        furnitureDelivery: HousingStateHelpers.getFurnitureDelivery(bedrooms)
                    })
                );
            })
        );
    });

    setDefaultUtilities$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(MarginCalculatorApiActions.marginLoadedSuccess),
            concatLatestFrom(() => [
                this.store.select(fromSelectors.selectUtilitiesPerMonth),
                this.store.select(fromSelectors.selectBedrooms),
                this.store.select(fromSelectors.selectContinuedHousing)
            ]),
            switchMap(([, utilities, bedrooms, continuedHousing]) => {
                if (utilities > 0) {
                    return of(
                        MarginCalculatorPageActions.noOp({
                            debug: 'setDefaultUtilities$ effect - utilities already set'
                        })
                    );
                } else if (continuedHousing) {
                    return of(
                        HousingActions.setUtilities({
                            utilities: 0
                        })
                    );
                }
                return of(
                    HousingActions.setUtilities({
                        utilities: HousingStateHelpers.getUtilities(bedrooms)
                    })
                );
            })
        );
    });

    setFurnitureAndUtilitiesOnContinuedHousing$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(HousingActions.setContinuedHousing),
            concatLatestFrom(() => [
                this.store.select(fromSelectors.selectContinuedHousing),
                this.store.select(fromSelectors.selectBedrooms)
            ]),
            concatMap(([, continuedHousing, bedrooms]) => {
                if (continuedHousing) {
                    return of(
                        HousingActions.setFurnitureUtilitiesExpenses({
                            moveOutExpenses: 0,
                            furnitureDelivery: 0,
                            furniture: HousingStateHelpers.getFurniture(bedrooms),
                            utilities: HousingStateHelpers.getUtilities(bedrooms)
                        })
                    );
                }
                return of(
                    HousingActions.setFurnitureUtilitiesExpenses({
                        moveOutExpenses: HousingStateHelpers.getMoveOutExpenses(bedrooms),
                        furnitureDelivery: HousingStateHelpers.getFurnitureDelivery(bedrooms),
                        furniture: HousingStateHelpers.getFurniture(bedrooms),
                        utilities: HousingStateHelpers.getUtilities(bedrooms)
                    })
                );
            })
        );
    });

    resetMovingDatesForContinuedInsurance$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(HousingActions.setContinuedHousing),
            concatLatestFrom(() => [
                this.store.select(fromSelectors.selectContinuedHousing),
                this.store.select(fromSelectors.selectStartDateAsDate),
                this.store.select(fromSelectors.selectEndDateAsDate)
            ]),
            concatMap(([, continuedHousing, startDate, endDate]) => {
                if (continuedHousing) {
                    const moveInDate = HousingStateHelpers.getMoveInDate(startDate);
                    const moveOutDate = HousingStateHelpers.getMoveOutDate(endDate);
                    return of(HousingActions.resetHousingFields({ moveInDate, moveOutDate }));
                }

                return of(
                    MarginCalculatorPageActions.noOp({
                        debug: 'resetMovingDatesForContinuedInsurance$ effect -continuedHousing is false'
                    })
                );
            })
        );
    });

    resetMonthlyRentForContinuedInsurance$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(HousingActions.setContinuedHousing),
            concatLatestFrom(() => [this.store.select(fromSelectors.selectContinuedHousing)]),
            concatMap(([, continuedHousing]) => {
                if (continuedHousing) {
                    return of(HousingActions.setMonthlyRent({ monthlyHousing: 0, extendedStayHotel: 0 }));
                }

                return of(
                    MarginCalculatorPageActions.noOp({
                        debug: 'resetMonthlyRentForContinuedInsurance$ effect -continuedHousing is false'
                    })
                );
            })
        );
    });

    constructor(
        private readonly actions$: Actions,
        private readonly store: Store
    ) {}
}
